/**
 * Client Portal API
 * Client Portal OpenAPI specification
 *
 * NOTE: This class is auto generated by OpenAPI Generator (https://openapi-generator.tech).
 * https://openapi-generator.tech
 * Do not edit the class manually.
 */

import {createAction} from '@reduxjs/toolkit';
import {ApiMethods, ConcurrentStrategies} from '@pb/middleware-api';
import {PayloadData} from '../types';
import type {CreateVendorRequest} from '../models/CreateVendorRequest';
import type {Error} from '../models/Error';
import type {UpdateVendorRequest} from '../models/UpdateVendorRequest';
import type {Vendor} from '../models/Vendor';
import type {VendorUserCount} from '../models/VendorUserCount';

export interface CreateVendor$Request {
    createVendorRequest: CreateVendorRequest;
}

export interface CreateVendor$Config {
    bulk?: boolean;
    concurrentStrategy?: typeof ConcurrentStrategies[keyof typeof ConcurrentStrategies];
    signal?: AbortSignal;
}

export const createVendorRequest = createAction<PayloadData<void, CreateVendor$Request>>('VENDOR_MANAGEMENT__CREATE_VENDOR__REQUEST');
export const createVendorSuccess = createAction<PayloadData<Vendor, CreateVendor$Request>>('VENDOR_MANAGEMENT__CREATE_VENDOR__SUCCESS');
export const createVendorFailure = createAction<PayloadData<Error, CreateVendor$Request, true>>('VENDOR_MANAGEMENT__CREATE_VENDOR__FAILURE');

export const createVendorActions = [
    createVendorRequest,
    createVendorSuccess,
    createVendorFailure
] as const;

/**
 * Create a new vendor.
 * @param {CreateVendorRequest} meta.createVendorRequest
 * @param {boolean} [config.bulk]
 * @param {typeof ConcurrentStrategies[keyof typeof ConcurrentStrategies]} [config.concurrentStrategy]
 * @param {AbortSignal} [config.signal]
 */
export const createVendor = (meta: CreateVendor$Request, config?: CreateVendor$Config) => {
    return {
        CALL_API: {
            types: createVendorActions,
            method: ApiMethods.POST,
            endpoint: `/web-api/vendors`,
            TRANSPORT_REQUEST_BODY: meta.createVendorRequest,
            bulk: config?.bulk,
            concurrentStrategy: config?.concurrentStrategy,
            signal: config?.signal,
            headers: {
                'Content-Type': 'application/json',
                'Accept': 'application/json',
            }
        },
        ...meta
    }
};

export interface DeleteVendor$Request {
    id: string;
}

export interface DeleteVendor$Config {
    bulk?: boolean;
    concurrentStrategy?: typeof ConcurrentStrategies[keyof typeof ConcurrentStrategies];
    signal?: AbortSignal;
}

export const deleteVendorRequest = createAction<PayloadData<void, DeleteVendor$Request>>('VENDOR_MANAGEMENT__DELETE_VENDOR__REQUEST');
export const deleteVendorSuccess = createAction<PayloadData<void, DeleteVendor$Request>>('VENDOR_MANAGEMENT__DELETE_VENDOR__SUCCESS');
export const deleteVendorFailure = createAction<PayloadData<Error, DeleteVendor$Request, true>>('VENDOR_MANAGEMENT__DELETE_VENDOR__FAILURE');

export const deleteVendorActions = [
    deleteVendorRequest,
    deleteVendorSuccess,
    deleteVendorFailure
] as const;

/**
 * Delete vendor by identifier.
 * @param {string} meta.id Vendor identifier
 * @param {boolean} [config.bulk]
 * @param {typeof ConcurrentStrategies[keyof typeof ConcurrentStrategies]} [config.concurrentStrategy]
 * @param {AbortSignal} [config.signal]
 */
export const deleteVendor = (meta: DeleteVendor$Request, config?: DeleteVendor$Config) => {
    return {
        CALL_API: {
            types: deleteVendorActions,
            method: ApiMethods.DELETE,
            endpoint: `/web-api/vendors/${meta.id}`,
            bulk: config?.bulk,
            concurrentStrategy: config?.concurrentStrategy,
            signal: config?.signal,
            headers: {
                'Accept': 'application/json',
            }
        },
        ...meta
    }
};

export interface GetAssignUsers$Request {
    vendorId: string;
}

export interface GetAssignUsers$Config {
    bulk?: boolean;
    concurrentStrategy?: typeof ConcurrentStrategies[keyof typeof ConcurrentStrategies];
    signal?: AbortSignal;
}

export const getAssignUsersRequest = createAction<PayloadData<void, GetAssignUsers$Request>>('VENDOR_MANAGEMENT__GET_ASSIGN_USERS__REQUEST');
export const getAssignUsersSuccess = createAction<PayloadData<Array<number>, GetAssignUsers$Request>>('VENDOR_MANAGEMENT__GET_ASSIGN_USERS__SUCCESS');
export const getAssignUsersFailure = createAction<PayloadData<Error, GetAssignUsers$Request, true>>('VENDOR_MANAGEMENT__GET_ASSIGN_USERS__FAILURE');

export const getAssignUsersActions = [
    getAssignUsersRequest,
    getAssignUsersSuccess,
    getAssignUsersFailure
] as const;

/**
 * get users IDs assigned to the vendor.
 * @param {string} meta.vendorId Vendor identifier
 * @param {boolean} [config.bulk]
 * @param {typeof ConcurrentStrategies[keyof typeof ConcurrentStrategies]} [config.concurrentStrategy]
 * @param {AbortSignal} [config.signal]
 */
export const getAssignUsers = (meta: GetAssignUsers$Request, config?: GetAssignUsers$Config) => {
    return {
        CALL_API: {
            types: getAssignUsersActions,
            method: ApiMethods.GET,
            endpoint: `/web-api/vendors/${meta.vendorId}/users`,
            bulk: config?.bulk,
            concurrentStrategy: config?.concurrentStrategy,
            signal: config?.signal,
            headers: {
                'Accept': 'application/json',
            }
        },
        ...meta
    }
};

export interface GetAssignUsersCount$Request {
    vendorId: string;
}

export interface GetAssignUsersCount$Config {
    bulk?: boolean;
    concurrentStrategy?: typeof ConcurrentStrategies[keyof typeof ConcurrentStrategies];
    signal?: AbortSignal;
}

export const getAssignUsersCountRequest = createAction<PayloadData<void, GetAssignUsersCount$Request>>('VENDOR_MANAGEMENT__GET_ASSIGN_USERS_COUNT__REQUEST');
export const getAssignUsersCountSuccess = createAction<PayloadData<VendorUserCount, GetAssignUsersCount$Request>>('VENDOR_MANAGEMENT__GET_ASSIGN_USERS_COUNT__SUCCESS');
export const getAssignUsersCountFailure = createAction<PayloadData<Error, GetAssignUsersCount$Request, true>>('VENDOR_MANAGEMENT__GET_ASSIGN_USERS_COUNT__FAILURE');

export const getAssignUsersCountActions = [
    getAssignUsersCountRequest,
    getAssignUsersCountSuccess,
    getAssignUsersCountFailure
] as const;

/**
 * get users count assigned to the vendor.
 * @param {string} meta.vendorId Vendor identifier
 * @param {boolean} [config.bulk]
 * @param {typeof ConcurrentStrategies[keyof typeof ConcurrentStrategies]} [config.concurrentStrategy]
 * @param {AbortSignal} [config.signal]
 */
export const getAssignUsersCount = (meta: GetAssignUsersCount$Request, config?: GetAssignUsersCount$Config) => {
    return {
        CALL_API: {
            types: getAssignUsersCountActions,
            method: ApiMethods.GET,
            endpoint: `/web-api/vendors/${meta.vendorId}/users/counts`,
            bulk: config?.bulk,
            concurrentStrategy: config?.concurrentStrategy,
            signal: config?.signal,
            headers: {
                'Accept': 'application/json',
            }
        },
        ...meta
    }
};

export interface GetVendorById$Request {
    id: string;
}

export interface GetVendorById$Config {
    bulk?: boolean;
    concurrentStrategy?: typeof ConcurrentStrategies[keyof typeof ConcurrentStrategies];
    signal?: AbortSignal;
}

export const getVendorByIdRequest = createAction<PayloadData<void, GetVendorById$Request>>('VENDOR_MANAGEMENT__GET_VENDOR_BY_ID__REQUEST');
export const getVendorByIdSuccess = createAction<PayloadData<Vendor, GetVendorById$Request>>('VENDOR_MANAGEMENT__GET_VENDOR_BY_ID__SUCCESS');
export const getVendorByIdFailure = createAction<PayloadData<Error, GetVendorById$Request, true>>('VENDOR_MANAGEMENT__GET_VENDOR_BY_ID__FAILURE');

export const getVendorByIdActions = [
    getVendorByIdRequest,
    getVendorByIdSuccess,
    getVendorByIdFailure
] as const;

/**
 * Retrieve vendor details by identifier.
 * @param {string} meta.id Vendor identifier
 * @param {boolean} [config.bulk]
 * @param {typeof ConcurrentStrategies[keyof typeof ConcurrentStrategies]} [config.concurrentStrategy]
 * @param {AbortSignal} [config.signal]
 */
export const getVendorById = (meta: GetVendorById$Request, config?: GetVendorById$Config) => {
    return {
        CALL_API: {
            types: getVendorByIdActions,
            method: ApiMethods.GET,
            endpoint: `/web-api/vendors/${meta.id}`,
            bulk: config?.bulk,
            concurrentStrategy: config?.concurrentStrategy,
            signal: config?.signal,
            headers: {
                'Accept': 'application/json',
            }
        },
        ...meta
    }
};

export interface GetVendors$Config {
    bulk?: boolean;
    concurrentStrategy?: typeof ConcurrentStrategies[keyof typeof ConcurrentStrategies];
    signal?: AbortSignal;
}

export const getVendorsRequest = createAction<PayloadData<void>>('VENDOR_MANAGEMENT__GET_VENDORS__REQUEST');
export const getVendorsSuccess = createAction<PayloadData<Array<Vendor>>>('VENDOR_MANAGEMENT__GET_VENDORS__SUCCESS');
export const getVendorsFailure = createAction<PayloadData<Error, void, true>>('VENDOR_MANAGEMENT__GET_VENDORS__FAILURE');

export const getVendorsActions = [
    getVendorsRequest,
    getVendorsSuccess,
    getVendorsFailure
] as const;

/**
 * Retrieve a list of vendors
 * @param {boolean} [config.bulk]
 * @param {typeof ConcurrentStrategies[keyof typeof ConcurrentStrategies]} [config.concurrentStrategy]
 * @param {AbortSignal} [config.signal]
 */
export const getVendors = (config?: GetVendors$Config) => {
    return {
        CALL_API: {
            types: getVendorsActions,
            method: ApiMethods.GET,
            endpoint: `/web-api/vendors`,
            bulk: config?.bulk,
            concurrentStrategy: config?.concurrentStrategy,
            signal: config?.signal,
            headers: {
                'Accept': 'application/json',
            }
        }
    }
};

export interface UpdateVendor$Request {
    id: string;
    updateVendorRequest: UpdateVendorRequest;
}

export interface UpdateVendor$Config {
    bulk?: boolean;
    concurrentStrategy?: typeof ConcurrentStrategies[keyof typeof ConcurrentStrategies];
    signal?: AbortSignal;
}

export const updateVendorRequest = createAction<PayloadData<void, UpdateVendor$Request>>('VENDOR_MANAGEMENT__UPDATE_VENDOR__REQUEST');
export const updateVendorSuccess = createAction<PayloadData<void, UpdateVendor$Request>>('VENDOR_MANAGEMENT__UPDATE_VENDOR__SUCCESS');
export const updateVendorFailure = createAction<PayloadData<Error, UpdateVendor$Request, true>>('VENDOR_MANAGEMENT__UPDATE_VENDOR__FAILURE');

export const updateVendorActions = [
    updateVendorRequest,
    updateVendorSuccess,
    updateVendorFailure
] as const;

/**
 * Update vendor information.
 * @param {string} meta.id Vendor identifier
 * @param {UpdateVendorRequest} meta.updateVendorRequest
 * @param {boolean} [config.bulk]
 * @param {typeof ConcurrentStrategies[keyof typeof ConcurrentStrategies]} [config.concurrentStrategy]
 * @param {AbortSignal} [config.signal]
 */
export const updateVendor = (meta: UpdateVendor$Request, config?: UpdateVendor$Config) => {
    return {
        CALL_API: {
            types: updateVendorActions,
            method: ApiMethods.PUT,
            endpoint: `/web-api/vendors/${meta.id}`,
            TRANSPORT_REQUEST_BODY: meta.updateVendorRequest,
            bulk: config?.bulk,
            concurrentStrategy: config?.concurrentStrategy,
            signal: config?.signal,
            headers: {
                'Content-Type': 'application/json',
                'Accept': 'application/json',
            }
        },
        ...meta
    }
};

